import {
  getActiveRecommenders,
  getMatchingRates,
} from "@engine/round/accessors";
import { scaleMvfProfile } from "@lib/matchingMvf";
import { type MVF, type MVFProfile, round } from "@lib/tranche";
import { getMarginalValue } from "algorithm/mvfs";
import mapValues from "lodash/mapValues";
import sum from "lodash/sum";
import { type RoundAggregate } from "./aggregate";
import { getDefaultMvfProfile } from "./initEvaluator";
import normalizeMvfProfile from "./normalizeMvfProfile";

type MeanRecommendations = {
  standard: MVFProfile;
  matching: MVFProfile;
};

export type Point = [number, number];

const meanRecommendationCache: {
  cacheForRound: RoundAggregate | null;
  cache: Map<number, MeanRecommendations>;
} = {
  cacheForRound: null,
  cache: new Map(),
};

/**
 * Interpolate the value at x for the line segment (p0, p1).
 */
function interpolate([x0, y0]: Point, [x1, y1]: Point, x: number): number {
  const m = (y1 - y0) / (x1 - x0);
  const b = y0 - m * x0;
  return m * x + b;
}

export function getMeanRecommendations(
  round: RoundAggregate,
  threshold: number, // How many evaluations there must be for a mean recommender to recommend an evaluable
): MeanRecommendations {
  if (meanRecommendationCache.cacheForRound !== round) {
    meanRecommendationCache.cacheForRound = round;
    meanRecommendationCache.cache = new Map();
  }

  const cache = meanRecommendationCache.cache;
  const cachedMvfProfile = cache.get(threshold);
  if (cachedMvfProfile) {
    return cachedMvfProfile;
  }

  const mvfProfile = getMeanRecommendationsInner(round, threshold);
  cache.set(threshold, mvfProfile);
  return mvfProfile;
}

function getMeanRecommendationsInner(
  round: RoundAggregate,
  threshold: number, // How many evaluations there must be for a mean recommender to recommend an evaluable
): MeanRecommendations {
  // Keep non-mean-recommenders
  const recommenders = getActiveRecommenders(round).filter(
    (recommender) => recommender?.meanRecommenderThreshold == null,
  );

  if (recommenders.length === 0) {
    const profile = getDefaultMvfProfile(round.holdProposalId);
    return {
      standard: profile,
      matching: profile,
    };
  }

  const normalizedStandardMvfProfiles = recommenders.map((recommender) =>
    normalizeMvfProfile(recommender.mvfProfile),
  );
  const normalizedMatchingMvfProfiles = recommenders.map((recommender) =>
    normalizeMvfProfile(
      scaleMvfProfile(
        recommender.mvfProfile,
        recommender.matchingMvfScales,
        getMatchingRates(round, recommender.mvfProfile),
      ),
    ),
  );

  for (let i = 0; i < recommenders.length; i++) {
    const recommender = recommenders[i];
    const nonMatchingMvfs = normalizedStandardMvfProfiles[i];
    const matchingMvfs = normalizedMatchingMvfProfiles[i];
    if (
      recommender == null ||
      nonMatchingMvfs == null ||
      matchingMvfs == null
    ) {
      continue;
    }
    for (const [evalId, evaluation] of Object.entries(
      recommender.evaluations,
    )) {
      if (evaluation.state !== "evaluated") {
        delete nonMatchingMvfs[evalId];
        delete matchingMvfs[evalId];
      }
    }
  }

  const nonMatching = normalizeMvfProfile(
    calculateRawMeanRecommendation(normalizedStandardMvfProfiles, threshold),
  );
  const matching = normalizeMvfProfile(
    calculateRawMeanRecommendation(normalizedMatchingMvfProfiles, threshold),
  );

  return {
    standard: nonMatching,
    matching,
  };
}

// Beware: the return value is not normalized.
export function calculateRawMeanRecommendation(
  normalizedMVFProfiles: MVFProfile[],
  threshold: number = 0,
  lastDollarThreshold: number = 1e9,
  integrationTarget: number = 1e9,
): MVFProfile {
  const evaluationCount: Record<string, number> = {};
  for (const mvfProfile of normalizedMVFProfiles) {
    for (const evaluableId of Object.keys(mvfProfile)) {
      evaluationCount[evaluableId] = (evaluationCount[evaluableId] || 0) + 1;
    }
  }

  const rescaledMVFProfiles = normalizedMVFProfiles.map((mvfProfile) =>
    rescaleRecommenderMVFs(mvfProfile, lastDollarThreshold, integrationTarget),
  );

  const meanRecommenderMVFProfile: MVFProfile = {};
  for (const evaluableId of Object.keys(evaluationCount)) {
    const count = evaluationCount[evaluableId] ?? 0;
    if (count < threshold) {
      continue;
    }

    const mvfs = rescaledMVFProfiles
      .map((mvfProfile) => mvfProfile[evaluableId])
      .filter((mvf): mvf is MVF => mvf != null);
    meanRecommenderMVFProfile[evaluableId] = getMeanMVF(mvfs);
  }

  return meanRecommenderMVFProfile;
}

export function getMeanMVF(mvfs: MVF[]): MVF {
  const positionsToSampleSet = new Set<number>();
  for (const mvf of mvfs) {
    for (const point of mvf) {
      positionsToSampleSet.add(point.dollar);
    }
  }
  const positionsToSample = Array.from(positionsToSampleSet).sort(
    (a, b) => a - b,
  );

  const meanMVF: MVF = [];
  for (const dollar of positionsToSample) {
    const value = getAverageValueAtDollar(mvfs, dollar);

    meanMVF.push({
      dollar,
      value,
      placedManually: true,
    });

    if (value === 0) {
      break;
    }
  }

  // if the last position to sample is a vertical jump, then the last point will
  // not be on the x-axis
  const lastPoint = meanMVF[meanMVF.length - 1];
  if (lastPoint != null && lastPoint.value !== 0) {
    meanMVF.push({
      dollar: lastPoint.dollar,
      value: 0,
      placedManually: true,
    });
  }

  return meanMVF;
}

function getAverageValueAtDollar(mvfs: MVF[], dollar: number): number {
  const totalValue = sum(mvfs.map((mvf) => getMarginalValue(dollar, mvf)));

  return round(totalValue / mvfs.length, 3);
}

export function getMvfProfileTotalSquaredMvfValue(
  mvfProfile: MVFProfile,
  lastDollarThreshold: number = 1e9,
): number {
  return sum(
    Object.values(mvfProfile).map((mvf) =>
      integrateSquaredMVF(mvf, lastDollarThreshold),
    ),
  );
}

export function rescaleRecommenderMVFs(
  recommenderMVFs: MVFProfile,
  lastDollarThreshold: number = 1e9,
  integrationTarget: number = 1e9,
): MVFProfile {
  const multiplier =
    integrationTarget /
    getMvfProfileTotalSquaredMvfValue(recommenderMVFs, lastDollarThreshold);

  return mapValues(recommenderMVFs, (mvf) => rescaleMVF(mvf, multiplier));
}

export function rescaleMVF(mvf: MVF, multiplier: number): MVF {
  return mvf.map((point) => ({
    ...point,
    value: point.value * multiplier,
  }));
}

/**
 * Utility function that integrates y^2 for the line segment from p0 to p1.
 * y = m*x + b, so y^2 = m^2 x^2 + 2 m b x + b^2
 * Antiderivative: (m^2/3) x^3 + m b x^2 + b^2 x
 */
function integrateSquaredMVFSegment([x0, y0]: Point, [x1, y1]: Point): number {
  if (x0 === x1) {
    return 0;
  }

  const m = (y1 - y0) / (x1 - x0);
  const b = y0;

  function antideriv(x: number): number {
    return (m ** 2 / 3) * x ** 3 + m * b * x ** 2 + b ** 2 * x;
  }

  return antideriv(x1 - x0);
}

/**
 * Integrate y^2 over a piecewise linear MVF from 0 to lastDollarThreshold.
 * MVF is assumed to be an array of { dollar, value } objects.
 */
export function integrateSquaredMVF(
  mvf: MVF,
  lastDollarThreshold: number,
): number {
  let result = 0;
  for (let i = 0; i < mvf.length - 1; i++) {
    const point = mvf[i];
    const nextPoint = mvf[i + 1];
    if (point == null || nextPoint == null) {
      continue;
    }
    const p0: Point = [point.dollar, point.value];
    let p1: Point = [nextPoint.dollar, nextPoint.value];

    if (p0[0] >= lastDollarThreshold) {
      break;
    }

    if (p1[0] > lastDollarThreshold) {
      p1 = [lastDollarThreshold, interpolate(p0, p1, lastDollarThreshold)];
    }

    result += integrateSquaredMVFSegment(p0, p1);
  }
  return result;
}
